// Whether a task's reminder reached a device (CER-129). The reminder's
// notification row (kind 'reminder', the task, fire_at = its remind_at) and
// its deliveries, one per device, say what happened; the words both apps show
// on the task screen are made here, once.

import type { Task } from './types';

export const DELIVERY_STATUSES = ['pending', 'sending', 'sent', 'retry', 'failed', 'expired', 'cancelled'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** One device's delivery of the reminder. error_code is an FCM error enum or a short internal code. */
export interface ReminderDeliveryAttempt {
  status: DeliveryStatus;
  error_code: string | null;
}

/** The reminder's notification: why it had no delivery at all (null when it had some), and its deliveries. */
export interface ReminderDelivery {
  undelivered_reason: string | null;
  deliveries: ReminderDeliveryAttempt[];
}

/** What the apps select from notifications for one reminder: its reason, and each delivery's status and error. */
export const REMINDER_DELIVERY_SELECT = 'undelivered_reason, notification_deliveries(status, error_code)';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isStatus = (value: unknown): value is DeliveryStatus => DELIVERY_STATUSES.some((status) => status === value);
const isTextOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';

/**
 * The reminder's notification row as the select above returns it: null when
 * there is none yet (the scheduler queues a reminder within a minute of its
 * time). Throws when it is not one, so a shape the apps do not expect is an
 * error to show, never a reminder quietly shown as fine.
 */
export function parseReminderDelivery(data: unknown): ReminderDelivery | null {
  if (data === null) return null;
  const malformed = () => new Error("the reminder's delivery record did not arrive");
  if (!isRecord(data) || !isTextOrNull(data.undelivered_reason) || !Array.isArray(data.notification_deliveries)) throw malformed();
  const deliveries = data.notification_deliveries.map((d): ReminderDeliveryAttempt => {
    if (!isRecord(d) || !isStatus(d.status) || !isTextOrNull(d.error_code)) throw malformed();
    return { status: d.status, error_code: d.error_code };
  });
  return { undelivered_reason: data.undelivered_reason, deliveries };
}

/** Whether there is a delivery to look up: a reminder is set and its time has come. */
export function reminderHasFallenDue(task: Pick<Task, 'remind_at'>, now: Date): task is Pick<Task, 'remind_at'> & { remind_at: string } {
  return task.remind_at !== null && new Date(task.remind_at).getTime() <= now.getTime();
}

const UNDELIVERED_REASONS: Record<string, string> = { no_device: 'no device received it' };

/**
 * The task screen's one line when the reminder did not reach any device;
 * null when it did, when it is still on its way, or when there is nothing to
 * say. A reminder queued with no device says it was not sent; one whose every
 * delivery failed, expired or was cancelled says it was not delivered, and
 * why. A done task whose reminder was cancelled (completing it cancels it)
 * says nothing.
 */
export function reminderDeliveryNote(delivery: ReminderDelivery | null, task: Pick<Task, 'status'>): string | null {
  if (delivery === null) return null;
  const { deliveries, undelivered_reason: reason } = delivery;
  if (deliveries.length === 0) {
    return reason === null ? null : `Reminder not sent: ${UNDELIVERED_REASONS[reason] ?? reason}`;
  }
  const settled = deliveries.every((d) => d.status === 'failed' || d.status === 'expired' || d.status === 'cancelled');
  if (!settled) return null;
  const has = (status: DeliveryStatus) => deliveries.some((d) => d.status === status);
  if (task.status === 'done' && deliveries.every((d) => d.status === 'cancelled')) return null;
  const codes = [...new Set(deliveries.filter((d) => d.status === 'failed' && d.error_code !== null).map((d) => d.error_code))];
  const reasons = [
    has('failed') ? `sending failed${codes.length > 0 ? ` (${codes.join(', ')})` : ''}` : null,
    has('expired') ? 'it could not be sent within the hour after its time' : null,
    has('cancelled') ? 'it was cancelled before it was sent' : null,
  ].filter((part): part is string => part !== null);
  return `Reminder not delivered: ${reasons.join('; ')}`;
}
